import { todayISO } from "@/engine/date";
import { useCatalogStore } from "@/store/catalog-store";
import { useProjectStore } from "@/store/project-store";
import { UI_TERMS } from "./terms";
import { can, planActor, type MemberPlanRole, type PlanLifecycle } from "./can";
import { useMockPlanAccess } from "./mock-plan-access";
import { isOwnExecutionTask, isOwnScheduleTask, type SelfRef } from "./own-assignment";
import { planSurface } from "./plan-surface";
import { readsPublicPlan, usePlanVisibility } from "./plan-visibility";
import { usePlanViewerComment } from "./plan-viewer-comment";
import { useWorkspaceRole } from "./workspace-access";

const ROLE_LABEL: Record<MemberPlanRole, string> = {
  owner: UI_TERMS.owner.ko,
  admin: UI_TERMS.admin.ko,
  editor: UI_TERMS.editor.ko,
  contributor: UI_TERMS.contributor.ko,
  viewer: UI_TERMS.viewerRole.ko,
};

export function planRoleLabel(role: MemberPlanRole): string {
  return ROLE_LABEL[role];
}

/** 워크스페이스 관리자 미리보기. 접근을 허용한 플랜만 열람자로 연다. */
export function useWorkspaceAdminView(): "off" | "meta" | "granted" {
  const on = useProjectStore((s) => s.previewWorkspaceAdmin);
  const planId = useProjectStore((s) => s.activeId);
  const grant = useMockPlanAccess().grants[planId ?? ""];
  if (!on || !planId) return "off";
  if (grant && grant.until >= todayISO()) return "granted";
  return "meta";
}

/** 로컬 플랜의 실제 역할은 소유자. 미리보기가 있으면 그 역할의 기본 플래그를 쓴다. */
export function usePlanActor() {
  const preview = useProjectStore((s) => s.previewPlanRole);
  const view = useWorkspaceAdminView();
  const workspaceRole = useWorkspaceRole();
  const planId = useProjectStore((s) => s.activeId);
  const visibility = usePlanVisibility(planId);
  if (view === "granted") return planActor("viewer");
  if (readsPublicPlan(visibility, workspaceRole)) return planActor("viewer");
  return planActor(preview ?? "owner");
}

export function useOpenPlanLifecycle(): PlanLifecycle {
  const open = useProjectStore((s) => !!s.activeId);
  const lifecycle = useProjectStore((s) => s.project.lifecycle);
  return open ? (lifecycle ?? "active") : "active";
}

export function useCan(actionId: string): boolean {
  const actor = usePlanActor();
  const status = useOpenPlanLifecycle();
  const planId = useProjectStore((s) => s.activeId);
  const viewerCanComment = usePlanViewerComment(planId);
  return can(actor, actionId, { status }, { viewerCanComment });
}

export function usePlanSurface() {
  const actor = usePlanActor();
  const status = useOpenPlanLifecycle();
  return planSurface(actor, status);
}

export function useSelfRef(): SelfRef {
  const resourceId = useProjectStore((s) => s.previewSelfResourceId);
  const name = useCatalogStore((s) => (resourceId ? s.resources[resourceId]?.displayName ?? null : null));
  return { resourceId, name };
}

/** 기여자는 고른 사람의 배정만 고친다. 다른 역할은 제한하지 않는다. */
export function useOwnWork() {
  const role = usePlanActor().role;
  const self = useSelfRef();
  const restricted = role === "contributor";
  return {
    restricted,
    self,
    scheduleOwn(task: { assignee?: string; assignedResourceId?: string }) {
      return !restricted || isOwnScheduleTask(task, self);
    },
    executionOwn(assignees: readonly string[]) {
      return !restricted || isOwnExecutionTask(assignees, self);
    },
    canCreateTask: !restricted || !!self.name,
  };
}

export function canCreateOwnTaskNow(): boolean {
  const { previewPlanRole, previewSelfResourceId } = useProjectStore.getState();
  if (previewPlanRole !== "contributor") return true;
  if (!previewSelfResourceId) return false;
  return !!useCatalogStore.getState().resources[previewSelfResourceId]?.displayName;
}

/**
 * 금액 표시.
 * 권한이 없으면 가리고 다시 열 수 없다.
 * 권한이 있으면 `maskRates`는 발표용 숨김이다.
 */
export function useMoneyMasked(): { masked: boolean; canReveal: boolean } {
  const canReveal = useCan("plan.cost.read");
  const maskRates = useProjectStore((s) => s.ui.maskRates);
  return { masked: !canReveal || maskRates, canReveal };
}
